import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import * as SupplierConditionsActions from '../conditions/store/supplier-conditions-actions';
import * as SupplierExternalCustomerMappingsActions from '../external-customer-mapping/store/supplier-external-customer-mapping-actions';
import * as SupplierOverViewActions from '../overview/store/supplier-detail-overview-actions';
import { Api } from '../../../../../api';
import { AppState } from '../../../../../store/models/app-state';
import { PALO_MASTER_DATA_LABELS } from '../../../../../common/components/palo/palo-master-data-labels';
import { ReduxDispatch } from '../../../../../store/use-thunk-dispatch';
import { SaveResult } from '../../../../../common/models/save-result';
import { paloMissingFieldsMessage } from '../../../../../common/components/palo/palo-missing-fields-message';
import { paloSchemaErrors } from '../../../../../common/components/palo/palo-schema-errors';
import { supplierDetailConditionsValidationSchema } from '../conditions/supplier-detail-conditions-validation-schema';
import { supplierDetailIsValidSelector } from './supplier-detail-selectors';

export const SUPPLIER_DETAIL_RESET = 'SUPPLIER_DETAIL_RESET';
export const SUPPLIER_DETAIL_SAVE = 'SUPPLIER_DETAIL_SAVE';
export const SUPPLIER_DETAIL_DELETE = 'SUPPLIER_DETAIL_DELETE';
export const SUPPLIER_DETAIL_TOGGLE_HIDE_PRODUCTS = 'SUPPLIER_DETAIL_TOGGLE_HIDE_PRODUCTS';
export const SUPPLIER_DETAIL_TOGGLE_LOCK_SUPPLIER = 'SUPPLIER_DETAIL_TOGGLE_LOCK_SUPPLIER';

export const resetSupplierDetails = () => ({ type: SUPPLIER_DETAIL_RESET });

export const deleteSupplier = () => {
    return async (dispatch: ReduxDispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: SUPPLIER_DETAIL_DELETE });

        const id = getState().contactsAndOrganizations.suppliers.supplierDetail.overview.supplier?.id;
        if (!id) {
            return;
        }

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.delete(id);
        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.supplierDeleted')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

/**
 * What the conditions tab calls its fields.
 *
 * None of them has an app.label entry, so the refusal would name them the way
 * the code does — "taxRateId" instead of "Gegenkonto".
 */
const CONDITION_LABELS: Record<string, string> = {
    taxRateId: 'app.common.counterAccount',
    bonusTaxRateId: 'app.contactsAndOrganizations.detail.bonusTaxRate',
    paymentTermId: 'app.contactsAndOrganizations.detail.paymentTerm',
    paymentMethod: 'app.contactsAndOrganizations.detail.paymentMethod',
    deliveryTermId: 'app.contactsAndOrganizations.detail.deliveryTerm',
};

export const saveSupplierDetails = () => {
    return async (dispatch: ReduxDispatch, getState: () => AppState) => {
        dispatch({ type: SUPPLIER_DETAIL_SAVE });

        const state = getState();
        const isAllValid = supplierDetailIsValidSelector(state);
        const supplierState = state.contactsAndOrganizations.suppliers.supplierDetail;
        const externalMappingState =
            state.contactsAndOrganizations.suppliers.supplierDetail.supplierExternalCustomerMappings;
        const supplierId = state.contactsAndOrganizations.suppliers.supplierDetail.overview.supplier?.id;

        // The conditions tab reports itself only once it has been mounted, so a
        // supplier could be saved without a Gegenkonto or a payment term as
        // long as nobody opened it (#2212, R-28). Its values are in the store
        // from the moment the record is loaded; this applies the tab's own
        // rules to them from here. A supplier that has no id yet cannot reach
        // the tab, so nothing is asked of it.
        //
        // Measured before switching this on: of 538 suppliers with an order or
        // an invoice in the last 24 months, 12 lack a payment term and 6 a
        // Gegenkonto or a Sach/Steuerkonto. The 2.264 of the whole table are
        // records nothing has moved in for years.
        const conditionErrors = supplierId
            ? paloSchemaErrors(supplierDetailConditionsValidationSchema, supplierState.conditions.detailConditions)
            : {};
        const conditionsComplete = 0 === Object.keys(conditionErrors).length;

        if (!isAllValid || !conditionsComplete) {
            // Which fields, and on which tab — see paloMissingFieldsMessage.
            const missing = paloMissingFieldsMessage([
                {
                    tab: 'app.contactsAndOrganizations.detail.tab.details',
                    errors: supplierState.overview.overviewErrors,
                    labels: { ...PALO_MASTER_DATA_LABELS, name: 'app.label.organisationName' },
                },
                {
                    tab: 'app.contactsAndOrganizations.detail.tab.conditions',
                    errors: { ...conditionErrors, ...supplierState.conditions.errors },
                    labels: CONDITION_LABELS,
                },
            ]);

            dispatch(
                Snackbar.enqueue(
                    missing
                        ? PoolNotification.error('app.snackbar.formErrorsNamed', { fields: missing })
                        : PoolNotification.error('app.snackbar.formErrors')
                )
            );
            return;
        }

        const promises = []; // : Promise<SaveResult | PayloadAction<SuccessResult<ExternalCustomerMappingFullResponse[]>>>[]

        if (supplierState.overview.isDirty) {
            promises.push(dispatch(SupplierOverViewActions.saveSupplierDetail()));
        }
        if (supplierState.conditions.isDirty) {
            promises.push(dispatch(SupplierConditionsActions.saveSupplierConditions()));
        }

        if (externalMappingState.isDirty && supplierId && externalMappingState.externalCustomerMapping) {
            promises.push(
                dispatch(
                    SupplierExternalCustomerMappingsActions.saveExternalCustomerMappings({
                        supplierId,
                        externalCustomerMappings: externalMappingState.externalCustomerMapping,
                    })
                )
            );
        }
        // dispatch other save actions here

        // Nothing dirty means nothing was written. On a record that exists that
        // is simply a no-op, but on a new one it means the form was never
        // filled in — and "Gespeichert" would claim a record that does not
        // exist (#2212, seen on an untouched new person).
        if (0 === promises.length) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.formErrors')));

            return;
        }

        const results = await Promise.all(promises);
        if (
            results.every(result =>
                typeof result === 'object' && 'meta' in result
                    ? result.meta?.requestStatus === 'fulfilled'
                    : result === SaveResult.Success
            )
        ) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.saved')));
        } else if (results.some(result => result === SaveResult.FormErrors)) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.formErrors')));
        } else if (!results.some(result => result === SaveResult.ErrorReported)) {
            // Whoever returned ErrorReported has already said what went wrong.
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const toggleHideProducts = () => {
    return async (dispatch: ReduxDispatch, getState: () => AppState, { api }: { api: Api }) => {
        const supplier = getState().contactsAndOrganizations.suppliers.supplierDetail.overview.supplier;
        if (!supplier?.id) {
            return;
        }
        dispatch({ type: SUPPLIER_DETAIL_TOGGLE_HIDE_PRODUCTS });

        const response = supplier.hideProducts
            ? await api.contactsAndOrganizations.Supplier.SupplierDetail.showProducts(supplier.id)
            : await api.contactsAndOrganizations.Supplier.SupplierDetail.hideProducts(supplier.id);

        if (response.success) {
            dispatch(
                supplier.hideProducts
                    ? Snackbar.enqueue(PoolNotification.success('app.contactsAndOrganizations.supplier.products.shown'))
                    : Snackbar.enqueue(
                          PoolNotification.success('app.contactsAndOrganizations.supplier.products.hidden')
                      )
            );
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const toggleLockSupplier = () => {
    return async (dispatch: ReduxDispatch, getState: () => AppState, { api }: { api: Api }) => {
        const supplier = getState().contactsAndOrganizations.suppliers.supplierDetail.overview.supplier;
        if (!supplier?.id) {
            return;
        }
        dispatch({ type: SUPPLIER_DETAIL_TOGGLE_LOCK_SUPPLIER });

        const response = supplier.isLocked
            ? await api.contactsAndOrganizations.Supplier.SupplierDetail.unlockSupplier(supplier.id)
            : await api.contactsAndOrganizations.Supplier.SupplierDetail.lockSupplier(supplier.id);

        if (response.success) {
            dispatch(
                supplier.isLocked
                    ? Snackbar.enqueue(
                          PoolNotification.success('app.contactsAndOrganizations.supplier.unlockSupplier.success')
                      )
                    : Snackbar.enqueue(
                          PoolNotification.success('app.contactsAndOrganizations.supplier.lockSupplier.success')
                      )
            );
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
